import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CATEGORIES, MAIN_SITE_URL } from "@/mocks/categories";

const LANGUAGES = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "da", label: "DA" },
  { code: "de", label: "DE" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  const isHome = location.pathname === "/";

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-background-50 border-b border-background-200">
      <nav className="flex items-center justify-between h-16 px-4 md:px-6">
        <Link
          to="/"
          className="flex items-center gap-2 whitespace-nowrap text-foreground-900"
        >
          <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-table-line text-lg" />
          </span>
          <span className="font-heading font-bold text-sm md:text-base tracking-tight">
            AllChinaBuy Spreadsheet
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          <NavLinkItem to="/" label={t("nav.home")} icon="ri-home-4-line" active={isHome} />
          <NavLinkItem to="/spreadsheet-guide" label={t("nav.guide")} icon="ri-book-open-line" active={location.pathname === "/spreadsheet-guide"} />
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Language switcher */}
          <div className="relative" ref={langRef}>
            <button
              type="button"
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-global-line text-base" />
              <span>{LANGUAGES.find((l) => l.code === i18n.language)?.label ?? "EN"}</span>
              <i className="ri-arrow-down-s-line text-sm" />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-28 rounded-lg border border-background-200 bg-background-50 py-1 z-50">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => changeLang(l.code)}
                    className={`w-full text-left px-3 py-2 text-sm whitespace-nowrap transition-colors hover:bg-background-100 ${
                      i18n.language === l.code
                        ? "text-primary-700 font-semibold"
                        : "text-foreground-700"
                    }`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
          >
            {t("nav.explore")}
            <i className="ri-arrow-right-up-line text-sm" />
          </a>

          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md text-foreground-900"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 px-4 py-3 flex flex-col gap-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-2.5 rounded-md text-foreground-800 hover:bg-background-100"
          >
            <i className="ri-home-4-line text-base text-primary-600" />
            {t("nav.home")}
          </Link>
          <Link
            to="/spreadsheet-guide"
            onClick={() => setMobileOpen(false)}
            className="flex items-center gap-2 px-3 py-2.5 rounded-md text-foreground-800 hover:bg-background-100"
          >
            <i className="ri-book-open-line text-base text-primary-600" />
            {t("nav.guide")}
          </Link>

          {/* Categories accordion */}
          <button
            type="button"
            onClick={() => setCatOpen((v) => !v)}
            className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-md text-foreground-800 hover:bg-background-100"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line text-base text-primary-600" />
              {t("categories.title")}
            </span>
            <i
              className={`ri-arrow-down-s-line text-base text-foreground-500 transition-transform ${
                catOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {catOpen && (
            <div className="grid grid-cols-2 gap-1 pl-3">
              {CATEGORIES.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-100"
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-700 shrink-0">
                    <i className={`${cat.icon} text-sm`} />
                  </span>
                  <span className="whitespace-nowrap truncate">{t(cat.labelKey)}</span>
                </a>
              ))}
            </div>
          )}

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap"
          >
            {t("nav.explore")}
            <i className="ri-arrow-right-up-line text-sm" />
          </a>
        </div>
      )}
    </header>
  );
}

function NavLinkItem({
  to,
  label,
  icon,
  active,
}: {
  to: string;
  label: string;
  icon: string;
  active: boolean;
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
        active
          ? "text-primary-700 bg-primary-100"
          : "text-foreground-700 hover:bg-background-100 hover:text-foreground-900"
      }`}
    >
      <i className={`${icon} text-base`} />
      {label}
    </Link>
  );
}